import { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Plus, Check, Loader2, Target, Calendar, Clock, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { formatDate } from "../lib/format";
import AzioneCicloForm from "./AzioneCicloForm";

/**
 * Bug 18/50 · Accordion di un CICLO con:
 *   - header: titolo, obiettivo, area, scadenza
 *   - progress bar % (fetch /tasks/{id}/progress)
 *   - prossima azione (da hint o derivata)
 *   - expand → lista azioni con checkbox completa + pulsante "Aggiungi azione"
 *
 * Progress e azioni caricati lazy quando l'accordion viene aperto per la prima volta.
 */
export default function CicloAccordion({ cycle, area, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [progress, setProgress] = useState(null);
  const [actions, setActions] = useState(null); // null = mai caricato
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);

  const loadProgress = useCallback(async () => {
    try {
      const p = await api.cycleProgress(cycle.id);
      setProgress(p);
    } catch { /* silent */ }
  }, [cycle.id]);

  const loadActions = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await api.listCycleActions(cycle.id, true);
      setActions(rows || []);
    } catch { setActions([]); }
    finally { setLoading(false); }
  }, [cycle.id]);

  // Progress viene caricato all'apertura della pagina (mostrato anche chiuso)
  useEffect(() => { loadProgress(); }, [loadProgress]);

  // Azioni caricate lazy quando l'accordion si apre la prima volta
  useEffect(() => {
    if (expanded && actions === null) loadActions();
  }, [expanded, actions, loadActions]);

  const toggleAction = async (a) => {
    try {
      await api.completeTask(a.id);
      toast.success(a.status === "completed" ? "Riaperta" : "✓ Azione completata");
      await Promise.all([loadActions(), loadProgress()]);
      onChanged?.();
    } catch { toast.error("Errore"); }
  };

  const onActionSaved = async () => {
    await Promise.all([loadActions(), loadProgress()]);
    onChanged?.();
  };

  const pct = progress?.pct ?? 0;
  const total = progress?.total ?? 0;
  const done = progress?.done ?? 0;
  const nextAction = progress?.next_action;

  return (
    <div className="pxos-card p-4" data-testid={`ciclo-accordion-${cycle.id}`}>
      {/* Header cliccabile */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-start gap-3 text-left"
        aria-expanded={expanded}
        data-testid={`ciclo-accordion-toggle-${cycle.id}`}
      >
        <span className="mt-1 opacity-60 shrink-0">
          {expanded ? <ChevronDown size={18}/> : <ChevronRight size={18}/>}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-display text-base font-semibold" data-testid={`ciclo-title-${cycle.id}`}>
              {cycle.title}
            </span>
            {area && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px]"
                style={{ background: (area.color || "#95BAEF") + "22", color: area.color || "#3d5a80" }}
                data-testid={`ciclo-area-${cycle.id}`}
              >
                <MapPin size={10}/> {area.name}
              </span>
            )}
            {cycle.due_date && (
              <span className="inline-flex items-center gap-1 text-[11px] opacity-70"
                    data-testid={`ciclo-due-${cycle.id}`}>
                <Calendar size={11}/> Scad. {formatDate(cycle.due_date)}
              </span>
            )}
            {cycle.scheduled_date && !cycle.due_date && (
              <span className="inline-flex items-center gap-1 text-[11px] opacity-70">
                <Calendar size={11}/> {formatDate(cycle.scheduled_date)}
              </span>
            )}
          </div>
          {cycle.objective_text && (
            <div className="text-xs mt-1 inline-flex items-center gap-1 opacity-80"
                 data-testid={`ciclo-objective-${cycle.id}`}>
              <Target size={11}/> {cycle.objective_text}
            </div>
          )}
          {/* Progress bar */}
          <div className="mt-2 flex items-center gap-2">
            <div className="flex-1 h-2 rounded-full bg-black/5 overflow-hidden"
                 data-testid={`ciclo-progress-bar-${cycle.id}`}>
              <div
                className="h-full transition-all"
                style={{
                  width: `${pct}%`,
                  background: pct >= 100 ? "#4a8a4f" : pct >= 50 ? "#95BAEF" : "#f28c28",
                }}
              />
            </div>
            <span className="text-[11px] tabular-nums opacity-70 shrink-0"
                  data-testid={`ciclo-progress-pct-${cycle.id}`}>
              {done}/{total} · {pct}%
            </span>
          </div>
          {nextAction && (
            <div className="text-[11px] opacity-70 mt-1 inline-flex items-center gap-1"
                 data-testid={`ciclo-next-action-${cycle.id}`}>
              <Clock size={10}/>
              Prossima azione: <strong className="font-medium">{nextAction.title}</strong>
              {nextAction.scheduled_date && !nextAction.from_hint && (
                <span className="opacity-70">· {formatDate(nextAction.scheduled_date)}</span>
              )}
              {nextAction.from_hint && (
                <span className="opacity-60 italic">(nota manuale)</span>
              )}
            </div>
          )}
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="mt-3 pl-6 border-l-2 space-y-2"
             style={{ borderColor: "var(--pxos-border)" }}
             data-testid={`ciclo-actions-list-${cycle.id}`}>
          {loading && <div className="opacity-70 text-sm flex items-center gap-2">
            <Loader2 size={14} className="animate-spin"/> Carico azioni…
          </div>}
          {!loading && actions !== null && actions.length === 0 && (
            <div className="opacity-70 text-sm" data-testid={`ciclo-actions-empty-${cycle.id}`}>
              Nessuna azione ancora. Aggiungi la prima azione per iniziare.
            </div>
          )}
          {!loading && actions && actions.map((a) => (
            <div key={a.id} className="flex items-start gap-2 py-1"
                 data-testid={`ciclo-action-row-${a.id}`}>
              <button
                onClick={() => toggleAction(a)}
                className="mt-0.5 w-4 h-4 rounded border-2 grid place-items-center hover:bg-green-50 shrink-0"
                style={{ borderColor: a.status === "completed" ? "#1F6B3A" : "var(--pxos-border-strong)" }}
                data-testid={`ciclo-action-toggle-${a.id}`}
                aria-label={a.status === "completed" ? "Riapri azione" : "Segna azione come completata"}
              >
                {a.status === "completed" && <Check size={10} className="text-green-700"/>}
              </button>
              <div className="flex-1 min-w-0 text-sm">
                <div className={a.status === "completed" ? "line-through opacity-50" : ""}>
                  {a.title}
                </div>
                <div className="text-[11px] opacity-70">
                  {a.scheduled_date ? (
                    <>
                      Programmata: {formatDate(a.scheduled_date)}
                      {a.scheduled_time ? ` · ${a.scheduled_time.slice(0,5)}` : ""}
                    </>
                  ) : (
                    <span className="italic">Senza data</span>
                  )}
                </div>
              </div>
            </div>
          ))}
          <button
            type="button" onClick={() => setFormOpen(true)}
            className="mt-2 pxos-btn-secondary text-xs inline-flex items-center gap-1"
            data-testid={`ciclo-add-action-btn-${cycle.id}`}
          >
            <Plus size={12}/> Aggiungi azione al ciclo
          </button>
        </div>
      )}

      <AzioneCicloForm
        open={formOpen}
        cycle={cycle}
        onClose={() => setFormOpen(false)}
        onSaved={onActionSaved}
      />
    </div>
  );
}
